/* eslint-disable no-shadow */

import ApiService from '@/services/api.service';
import {
 apiEndpoint,
  developerRoleId, translatorRoleId,
} from '@/shared';
import { client, collections } from '@/services/typesense.service';


const endpoint = `${apiEndpoint}/search`;

const state = {
  searchResults: null,
  pending: false,
  error: null,
  totalKeywords: null,
  readyLoadMoreKeywords: true,
};

const getters = {
  loaded: (state) => !state.pending && state.searchResults != null,
  searchResults: (state, getters) => (getters.loaded ? state.searchResults : []),
  totalKeywords: (state) => state.totalKeywords ?? null,
  readyLoadMoreKeywords: (state) => state.readyLoadMoreKeywords,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.searchResults = null;
    }
    state.error = null;
    state.pending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, { searchResults }) {
    state.pending = false;
    state.searchResults = searchResults;
  },
  totalKeywords(state, totalRows) {
    state.totalKeywords = totalRows;
  },
  readyLoadMoreKeywords(state, ready) {
    state.readyLoadMoreKeywords = ready;
  },
};

const actions = {
  async fetchData({ commit }, payload) {
    commit('beforeLoading');
    try {
      const result = await ApiService.get(`${endpoint}?search=${payload.search}`);
      const searchResults = result.data;
      commit('data', { searchResults });
    } catch (error) {
      commit('error', error);
    }
  },
  async searchRecords({ commit }, payload) {
    commit('beforeLoading');
    try {
    const filters = [];

    if ((payload.userRole === translatorRoleId || payload.userRole === developerRoleId)
      && payload.userLanguage !== null) {
      const projectIds = payload.userProjects.join(', ');
      filters.push(`project_id:=[${projectIds}]`);
      filters.push(`language_iso:=${payload.userLanguage.iso}`);
    }

    if (payload.userRole === developerRoleId && payload.userLanguage === null) {
      return;
    }

    const searchParameters = {
      q: payload.q,
      per_page: 20,
      filter_by: filters.join('&&'),
    };

      // Search Unique Projects
      searchParameters.query_by = 'project_key';
      searchParameters.group_by = 'project_id';
      searchParameters.group_limit = 1;

      const resProject = await client
        .collections(collections.keywords)
        .documents()
        .search(searchParameters);

      const uniqueProjects = resProject.grouped_hits.map((group) => {
        const doc = group.hits[0].document;
        return {
          id: doc.project_id,
          key: doc.project_key,
          name: doc.project_name,
          description: doc.project_name,
        };
      });

      const projectsResponse = {
        data: uniqueProjects,
      };

      // Search Unique Pages
      searchParameters.query_by = 'page_key';
      searchParameters.group_by = 'page_id';

      const resPage = await client
        .collections(collections.keywords)
        .documents()
        .search(searchParameters);

      const uniquePages = resPage.grouped_hits.map((group) => {
        const doc = group.hits[0].document;
        return {
          id: doc.page_id,
          key: doc.page_key,
          name: doc.page_name,
        };
      });

      const pagesResponse = {
        data: {
          items: uniquePages,
          totalRows: 0,
          project: null,
        },
      };

      // Unique Keywords
      searchParameters.query_by = 'keyword_translated';
      searchParameters.group_by = null;
      searchParameters.group_limit = null;

      const res = await client
        .collections(collections.keywords)
        .documents()
        .search(searchParameters);

      const uniqueKeywords = res.hits.map((hit) => {
        const doc = hit.document;
        return {
          typesenseId: doc.id,
          id: doc.keyword_id,
          key: doc.keyword_key,
          description: doc.keyword_description,
          project_page_id: doc.page_id,
          translation: doc.keyword_translated,
          projectId: doc.project_id,
          languageIso: doc.language_iso,
        };
      });

      const keywordsResponse = {
        data: {
          items: uniqueKeywords,
          totalRows: res.found,
          projectPage: null,
          project: '',
        },
      };
      commit('totalKeywords', res.found);

      const finalResponse = {
        projects: projectsResponse,
        pages: pagesResponse,
        keywords: keywordsResponse,
      };

      commit('data', { searchResults: finalResponse });
    } catch (error) {
      commit('error', error);
    }
  },

  async loadMoreKeywords({ commit }, payload) {
      try {
      const filters = [];

      if ((payload.userRole === translatorRoleId || payload.userRole === developerRoleId)
        && payload.userLanguage !== null) {
        const projectIds = payload.userProjects.join(', ');
        filters.push(`project_id:=[${projectIds}]`);
        filters.push(`language_iso:=${payload.userLanguage.iso}`);
      }

      if (payload.userRole === developerRoleId && payload.userLanguage === null) {
        return {
          items: [],
          totalRows: 0,
          projectPage: null,
          project: '',
        };
      }

      const searchParameters = {
        q: payload.q,
        per_page: 20,
        filter_by: filters.join('&&'),
      };

      searchParameters.query_by = 'keyword_translated';
      searchParameters.group_by = null;
      searchParameters.group_limit = null;
      searchParameters.page = payload.forPage;

      const res = await client
        .collections(collections.keywords)
        .documents()
        .search(searchParameters);

      const uniqueKeywords = res.hits.map((hit) => {
        const doc = hit.document;
        return {
          typesenseId: doc.id,
          id: doc.keyword_id,
          key: doc.keyword_key,
          description: doc.keyword_description,
          project_page_id: doc.page_id,
          translation: doc.keyword_translated,
          projectId: doc.project_id,
          languageIso: doc.language_iso,
        };
      });

      const keywordsResponse = {
        data: {
          items: uniqueKeywords,
          totalRows: res.found,
          projectPage: null,
          project: '',
        },
      };

      return keywordsResponse.data;
    } catch (error) {
      commit('error', error);
      return {
        items: [],
        totalRows: 0,
        projectPage: null,
        project: '',
      };
    }
  },
  async readyLoadMoreKeywords({ commit }, payload) {
    try {
      commit('readyLoadMoreKeywords', payload.readyToLoad);
    } catch (error) {
      commit('error', error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
